import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2, ArrowRight, Mail, Smartphone, IndianRupee,
  ShieldCheck, RefreshCw, Info, Sparkles, TrendingUp, MapPin, Percent, FileText,
} from "lucide-react";
import { findUpgradeSuggestion } from "@/lib/plans";

const STEPS = [
  { n: 1, label: "Your details" },
  { n: 2, label: "Verify" },
  { n: 3, label: "Payment" },
];

const IN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir", "Jharkhand", "Karnataka",
  "Kerala", "Ladakh", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman & Nicobar", "Chandigarh",
  "Dadra & Nagar Haveli", "Daman & Diu", "Lakshadweep", "Puducherry",
];

export default function Apply() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, authenticateFromToken } = useAuth();
  const [svc, setSvc] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(params.get("plan") || null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    pan: user?.pan || "",
    address: user?.address || "",
    additional_info: "",
    show_additional: false,
    billing_state: "",
  });
  // Step 3 (payment) state
  const [hasGst, setHasGst] = useState(false);
  const [gstin, setGstin] = useState("");
  const [hasPromo, setHasPromo] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [session, setSession] = useState(null);
  const [otpTab, setOtpTab] = useState("mobile");
  const [otpCode, setOtpCode] = useState("");
  const [order, setOrder] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const stepCardRef = useRef(null);

  // Auto-scroll to the active step card whenever we advance to Step 2 or 3.
  useEffect(() => {
    if (step === 1 || !stepCardRef.current) return;
    const el = stepCardRef.current;
    const y = el.getBoundingClientRect().top + window.scrollY - 90; // ~90px breathing room
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      const s = r.data.service;
      setSvc(s);
      if (s.plans && s.plans.length > 0) {
        const fromUrl = params.get("plan");
        const validBase = s.plans.find((p) => p.code === (fromUrl || "").split(":")[0]);
        const chosen = validBase || s.plans[0];
        const isValidity = chosen.pricing_type === "validity" || chosen.year_prices;
        if (fromUrl && fromUrl.includes(":")) {
          setSelectedPlan(fromUrl);
        } else if (isValidity) {
          setSelectedPlan(`${chosen.code}:1`);
        } else {
          setSelectedPlan(chosen.code);
        }
      }
    }).catch(() => setSvc(false));
  }, [slug, params]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const applyCoupon = async () => {
    const code = (couponInput || "").trim();
    if (!code) return;
    const baseAmount = order?.original_amount || order?.amount || 0;
    setApplyingCoupon(true);
    try {
      const { data } = await api.post(
        `/coupons/validate?code=${encodeURIComponent(code)}&amount=${baseAmount}&service_slug=${encodeURIComponent(slug)}`
      );
      setCouponApplied(data.code);
      setCouponDiscount(data.discount);
      toast.success(`Coupon applied — you save ₹${data.discount}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied("");
    setCouponDiscount(0);
    setCouponInput("");
  };

  const startDetails = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ""))) return toast.error("Enter a 10-digit mobile number");
    if (!form.billing_state) return toast.error("Please select your billing state");
    setBusy(true);
    try {
      const payload = {
        service_slug: slug, plan_code: selectedPlan,
        full_name: form.full_name, email: form.email.toLowerCase(),
        mobile: form.mobile.replace(/\D/g, ""), pan: form.pan, address: form.address,
        additional_info: form.show_additional ? form.additional_info : "",
      };
      if (user) {
        const { data } = await api.post("/orders", payload);
        // Save billing state to the order
        try {
          await api.put(`/orders/${data.order.id}/billing`, {
            order_id: data.order.id, has_gstin: false, gstin: "",
            billing_state: form.billing_state,
          });
          data.order.billing_state = form.billing_state;
        } catch {}
        setOrder(data.order);
        setStep(3);
      } else {
        const { data } = await api.post("/apply/start", payload);
        setSession(data);
        setCountdown(900);
        setResendCooldown(30);
        setStep(2);
        toast.success("Verification codes sent");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not proceed");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) return toast.error("Enter the OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/apply/verify", {
        session_id: session.session_id, otp_type: otpTab, otp_code: otpCode,
      });
      authenticateFromToken(data.token, data.user);
      // Save billing state to the newly-created order
      try {
        await api.put(`/orders/${data.order.id}/billing`, {
          order_id: data.order.id, has_gstin: false, gstin: "",
          billing_state: form.billing_state,
        });
        data.order.billing_state = form.billing_state;
      } catch {}
      setOrder(data.order);
      setIsNewUser(!!data.is_new_user);
      if (data.temp_password) setTempPassword(data.temp_password);
      setStep(3);
      toast.success(data.is_new_user ? "Account created — proceed to payment" : "Verified — proceed to payment");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    if (!session) return;
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-otp", {
        session_id: session.session_id,
        channel: otpTab,
      });
      if (data.dev_mobile_otp) {
        setSession((s) => ({ ...s, dev_mobile_otp: data.dev_mobile_otp }));
      }
      setCountdown(900);
      setResendCooldown(30);
      toast.success(`New ${otpTab} OTP sent`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      // Save GSTIN + billing state before creating payment session
      if (order?.id) {
        try {
          await api.put(`/orders/${order.id}/billing`, {
            order_id: order.id,
            has_gstin: !!hasGst,
            gstin: hasGst ? (gstin || "") : "",
            billing_state: form.billing_state || order.billing_state || "Rajasthan",
          });
        } catch (err) {
          if (hasGst && err?.response?.status === 400) {
            toast.error(err.response.data?.detail || "Invalid GSTIN");
            setBusy(false);
            return;
          }
        }
      }
      const { data } = await api.post("/payments/cashfree/create", {
        order_id: order.id, coupon_code: couponApplied || "",
      });
      const { load: cfload } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await cfload({ mode: data.mode === "production" ? "production" : "sandbox" });
      try {
        sessionStorage.setItem(`w2e_apply_${order.id}`, JSON.stringify({ temp_password: tempPassword, is_new_user: isNewUser }));
      } catch {}
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
      setBusy(false);
    }
  };

  if (svc === false) {
    return <PublicLayout><div className="max-w-3xl mx-auto py-24 text-center"><h1 className="font-display font-bold text-3xl">Service not found</h1><Link to="/services" className="text-[#1E3A8A] mt-4 inline-block">← Back</Link></div></PublicLayout>;
  }
  if (!svc) return <PublicLayout><div className="p-10 text-slate-500">Loading…</div></PublicLayout>;

  const hasPlans = svc.plans && svc.plans.length > 0;
  const activePlan = hasPlans ? (() => {
    const base = (selectedPlan || "").split(":")[0];
    return svc.plans.find((p) => p.code === base) || svc.plans[0];
  })() : null;
  // Parse encoded plan segments: year (integer) and qty (starts with "q").
  const planSegments = (selectedPlan || "").split(":").slice(1);
  const encodedYear = (() => {
    const seg = planSegments.find((s) => s && !s.toLowerCase().startsWith("q"));
    const n = parseInt(seg || "1", 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  })();
  const encodedQty = (() => {
    const seg = planSegments.find((s) => s && s.toLowerCase().startsWith("q"));
    if (!seg) return 1;
    const n = parseInt(seg.slice(1), 10);
    return Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 1;
  })();
  const qtyEnabled = !!(activePlan && activePlan.quantity_enabled);
  const effectiveQty = qtyEnabled ? encodedQty : 1;
  const unitPriceToShow = (() => {
    if (!activePlan) return svc.price;
    if (activePlan.pricing_type === "validity" || activePlan.year_prices) {
      const yp = activePlan.year_prices || {};
      return Number(yp[encodedYear] ?? yp[String(encodedYear)] ?? activePlan.price ?? 0);
    }
    return Number(activePlan.price || 0);
  })();
  const priceToShow = unitPriceToShow * effectiveQty;
  const buildPlanCode = (baseCode, year, qty) => {
    const parts = [baseCode];
    if (year) parts.push(String(year));
    if (qty && qty > 1) parts.push(`q${qty}`);
    return parts.join(":");
  };

  const mm = Math.floor(countdown / 60);
  const ss = String(countdown % 60).padStart(2, "0");

  const originalAmount = Number(order?.original_amount || order?.amount || 0);
  const finalAmount = Math.max(0, +(originalAmount - couponDiscount).toFixed(2));

  return (
    <PublicLayout>
      <section className="gradient-radial-blue">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to={`/service/${slug}`} className="text-sm text-slate-500 hover:text-[#1E3A8A]">← Back to service details</Link>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="section-label">Apply for</div>
              <h1 className="mt-1 font-display font-bold text-3xl sm:text-4xl text-[#0A192F]">{svc.name}</h1>
              <div className="mt-1 text-slate-600">Complete in 3 quick steps — no login needed.</div>
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-4 min-w-[180px]">
              <div className="text-xs uppercase tracking-widest text-slate-500">{activePlan ? activePlan.name : "Total fee"}</div>
              <div className="font-display font-bold text-2xl text-[#0A192F]" data-testid="apply-total-price">₹{Number(priceToShow).toLocaleString("en-IN")}</div>
              {qtyEnabled && effectiveQty > 1 && (
                <div className="text-[11px] text-slate-500 mt-0.5">₹{Number(unitPriceToShow).toLocaleString("en-IN")} × {effectiveQty}</div>
              )}
              <div className="text-xs text-slate-500 mt-0.5">Incl. taxes</div>
            </div>
          </div>

          {hasPlans && step === 1 && (
            <div className="mt-6" data-testid="apply-plan-selector">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                {svc.plans.length > 1 ? "Choose an option" : "Selected plan"}
              </div>
              {/* Tabs across plans */}
              {svc.plans.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="apply-plan-tabs">
                  {svc.plans.map((p) => {
                    const active = (selectedPlan || "").split(":")[0] === p.code;
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => {
                          if (p.pricing_type === "validity" || p.year_prices) {
                            setSelectedPlan(`${p.code}:1`);
                          } else {
                            setSelectedPlan(p.code);
                          }
                        }}
                        className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
                          active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
                        }`}
                        data-testid={`apply-plan-tab-${p.code}`}
                      >
                        {p.name}{p.popular ? " ★" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Selected plan panel */}
              {(() => {
                const baseCode = (selectedPlan || "").split(":")[0];
                const p = svc.plans.find((x) => x.code === baseCode) || svc.plans[0];
                if (!p) return null;
                const isValidity = p.pricing_type === "validity" || !!p.year_prices;
                const yearPrices = p.year_prices || {};
                const availableYears = [1, 2, 3, 4, 5].filter((y) => Number(yearPrices[y] ?? yearPrices[String(y)] ?? 0) > 0);
                const currentYear = encodedYear;
                const pQtyEnabled = !!p.quantity_enabled;
                const currentQty = pQtyEnabled ? encodedQty : 1;
                const unitPrice = isValidity
                  ? Number(yearPrices[currentYear] ?? yearPrices[String(currentYear)] ?? 0)
                  : Number(p.price || 0);
                return (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid={`apply-plan-panel-${p.code}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0A192F]">{p.name}</div>
                        {p.description && <div className="text-xs text-slate-600 mt-0.5">{p.description}</div>}
                      </div>
                      {!isValidity && (
                        <div className="font-display font-bold text-[#0A192F] shrink-0">₹{Number(p.price || 0).toLocaleString("en-IN")}</div>
                      )}
                    </div>
                    {isValidity && availableYears.length > 0 && (
                      <div className="mt-3" data-testid={`apply-year-radios-${p.code}`}>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Select year</div>
                        <div className="flex flex-wrap gap-2">
                          {availableYears.map((y) => {
                            const price = Number(yearPrices[y] ?? yearPrices[String(y)] ?? 0);
                            const active = currentYear === y;
                            return (
                              <label
                                key={y}
                                className={`cursor-pointer px-3 py-2 rounded-md border text-sm font-semibold flex items-center gap-2 transition-colors ${
                                  active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
                                }`}
                                data-testid={`apply-year-${p.code}-${y}`}
                              >
                                <input
                                  type="radio"
                                  className="accent-[#1E3A8A]"
                                  checked={active}
                                  onChange={() => setSelectedPlan(buildPlanCode(p.code, y, currentQty))}
                                />
                                Year {y}
                                <span className={active ? "text-white" : "text-[#0A192F]"}>· ₹{price.toLocaleString("en-IN")}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {pQtyEnabled && (
                      <div className="mt-3" data-testid={`apply-qty-wrap-${p.code}`}>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Quantity</div>
                        <div className="inline-flex items-center gap-0 rounded-md border border-slate-300 bg-white overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setSelectedPlan(buildPlanCode(p.code, isValidity ? currentYear : null, Math.max(1, currentQty - 1)))}
                            className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                            data-testid="apply-qty-decr"
                          >−</button>
                          <input
                            type="number"
                            min="1"
                            max="99"
                            value={currentQty}
                            onChange={(e) => {
                              const n = parseInt(e.target.value || "1", 10);
                              const clamped = Number.isFinite(n) ? Math.max(1, Math.min(99, n)) : 1;
                              setSelectedPlan(buildPlanCode(p.code, isValidity ? currentYear : null, clamped));
                            }}
                            className="w-16 text-center text-sm font-semibold outline-none border-x border-slate-300 py-2"
                            data-testid="apply-qty-input"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedPlan(buildPlanCode(p.code, isValidity ? currentYear : null, Math.min(99, currentQty + 1)))}
                            className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                            data-testid="apply-qty-incr"
                          >+</button>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          ₹{Number(unitPrice).toLocaleString("en-IN")} × {currentQty} = ₹{Number(unitPrice * currentQty).toLocaleString("en-IN")}
                        </div>
                      </div>
                    )}
                    {svc.pricing_note && (
                      <div className="text-[11px] text-slate-500 mt-3" data-testid="apply-pricing-note">{svc.pricing_note}</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {false && (
            <div>
              {(() => {
                const s = findUpgradeSuggestion(selectedPlan, svc.plans);
                if (!s) return null;
                return (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-3" data-testid="apply-upgrade-suggestion">
                    <TrendingUp className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" />
                    <div className="flex-1 text-xs">
                      <div className="font-semibold text-emerald-900">Save ₹{s.savings.toLocaleString("en-IN")} — upgrade to {s.plan.name}</div>
                      <div className="text-emerald-800 mt-0.5">{s.plan.duration_years} years for ₹{s.plan.price.toLocaleString("en-IN")} instead of ~₹{s.equivalent.toLocaleString("en-IN")}.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedPlan(s.plan.code)}
                      className="shrink-0 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md"
                      data-testid="apply-upgrade-cta"
                    >
                      Upgrade
                    </button>
                  </div>
                );
              })()}
            </div>
          )}

          <ol className="mt-8 grid grid-cols-3 gap-2 sm:gap-4" data-testid="apply-stepper">
            {STEPS.map((s) => {
              const active = step === s.n;
              const done = step > s.n;
              return (
                <li key={s.n} className={`p-3 rounded-lg border ${done ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : active ? "border-[#1E3A8A] bg-white ring-2 ring-[#1E3A8A]/30" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done || active ? "bg-[#1E3A8A] text-white" : "bg-slate-200 text-slate-600"}`}>
                      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Step {s.n}</span>
                  </div>
                  <div className={`text-sm font-semibold mt-1 ${done || active ? "text-[#0A192F]" : "text-slate-500"}`}>{s.label}</div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="section-label">Step 1</div>
              <h2 className="mt-1 font-display font-semibold text-2xl text-[#0A192F]">Enter your details</h2>
              {!user && (
                <div className="mt-3 p-3 rounded-md border border-blue-200 bg-blue-50 text-sm text-slate-700 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#1E3A8A]" />
                  <span>We'll verify your email or mobile with an OTP, then process payment. Your Way2Efiling account is created automatically — no separate signup needed.</span>
                </div>
              )}
              <form onSubmit={startDetails} className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="apply-details-form">
                <div>
                  <Label>Full Name</Label>
                  <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="apply-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-700 text-sm font-medium">+91</span>
                    <Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" inputMode="numeric" maxLength={10} className="rounded-l-none" data-testid="apply-mobile" />
                  </div>
                </div>
                <div>
                  <Label>PAN (optional)</Label>
                  <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" className="font-mono uppercase" data-testid="apply-pan" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address (optional)</Label>
                  <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="apply-address" />
                </div>

                {/* Billing State (required, professional select) */}
                <div className="sm:col-span-2">
                  <Label className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />
                    Billing State <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.billing_state} onValueChange={(v) => setForm({ ...form, billing_state: v })}>
                    <SelectTrigger data-testid="apply-billing-state" className="h-11 bg-white border-slate-300 hover:border-slate-400 focus:ring-2 focus:ring-[#1E3A8A]/30 transition-colors">
                      <SelectValue placeholder="Select your billing state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {IN_STATES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`apply-state-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-slate-500 mt-1">Rajasthan → CGST + SGST (9% + 9%) · Other states → IGST (18%)</div>
                </div>

                {/* Additional information toggle */}
                <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="apply-additional-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#0A192F]">Add additional information?</div>
                      <div className="text-xs text-slate-500 mt-0.5">Anything specific about your filing you'd like our team to know.</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold uppercase ${form.show_additional ? "text-[#1E3A8A]" : "text-slate-400"}`}>{form.show_additional ? "Yes" : "No"}</span>
                      <Switch
                        checked={form.show_additional}
                        onCheckedChange={(v) => setForm({ ...form, show_additional: v, additional_info: v ? form.additional_info : "" })}
                        data-testid="apply-additional-toggle"
                      />
                    </div>
                  </div>
                  {form.show_additional && (
                    <div className="mt-3">
                      <Textarea
                        rows={3}
                        value={form.additional_info}
                        onChange={(e) => setForm({ ...form, additional_info: e.target.value })}
                        placeholder="e.g. This is a revised return / I have HRA claim / Business turnover approx ₹40L..."
                        data-testid="apply-additional-textarea"
                      />
                    </div>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <Button type="submit" disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6" data-testid="apply-continue-btn">
                    {busy ? "Please wait…" : user ? "Continue to payment" : "Send OTP & continue"} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && session && (
            <div ref={stepCardRef} className="rounded-xl border border-slate-200 bg-white p-6" data-testid="apply-step2-card">
              <div className="section-label">Step 2</div>
              <h2 className="mt-1 font-display font-semibold text-2xl text-[#0A192F]">Verify to confirm your details</h2>
              <p className="text-sm text-slate-600 mt-1">
                We sent codes to <span className="font-medium">{session.email_masked}</span> and <span className="font-medium">+91 {session.mobile_masked}</span>. Verifying one is enough.
              </p>
              {session.has_existing_account && (
                <div className="mt-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  We found an existing Way2Efiling account with this email/mobile. This order will be linked to it.
                </div>
              )}

              <Tabs value={otpTab} onValueChange={(v) => { setOtpTab(v); setOtpCode(""); }} className="mt-5" data-testid="apply-otp-tabs">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="mobile" data-testid="apply-otp-tab-mobile"><Smartphone className="w-4 h-4 mr-1.5" /> Mobile</TabsTrigger>
                  <TabsTrigger value="email" data-testid="apply-otp-tab-email"><Mail className="w-4 h-4 mr-1.5" /> Email</TabsTrigger>
                </TabsList>
                <TabsContent value="email" className="mt-4">
                  <div className="text-xs text-slate-500 mb-2">Enter the 6-digit code from your email.</div>
                </TabsContent>
                <TabsContent value="mobile" className="mt-4">
                  <div className="text-xs text-slate-500 mb-2">Enter the 6-digit code sent to your mobile.</div>
                  {session.dev_mobile_otp && (
                    <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs" data-testid="apply-dev-otp">
                      <div className="font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Test Mode</div>
                      SMS provider not configured. Your OTP: <span className="font-mono font-bold text-sm text-[#0A192F]">{session.dev_mobile_otp}</span>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <form onSubmit={verifyOtp} className="mt-2 space-y-4" data-testid="apply-otp-form">
                <Input required value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="text-center text-2xl font-mono tracking-[0.4em] h-14" data-testid="apply-otp-input" />
                <Button type="submit" disabled={busy} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="apply-otp-verify-btn">
                  {busy ? "Verifying…" : "Verify & continue to payment"}
                </Button>
              </form>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>{countdown > 0 ? `Expires in ${mm}:${ss}` : "Code expired"}</span>
                <button type="button" onClick={resendOtp} disabled={resending || resendCooldown > 0} className="text-[#1E3A8A] font-medium hover:underline disabled:text-slate-400 disabled:no-underline inline-flex items-center gap-1" data-testid="apply-otp-resend-btn">
                  <RefreshCw className="w-3 h-3" /> {resending ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend"}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && order && (
            <div ref={stepCardRef} className="rounded-xl border border-slate-200 bg-white p-6" data-testid="apply-payment-step">
              <div className="section-label">Step 3</div>
              <h2 className="mt-1 font-display font-semibold text-2xl text-[#0A192F]">Complete payment</h2>

              {/* Order summary card */}
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Service:</span> <span className="font-medium">{order.service_name}</span></div>
                  <div><span className="text-slate-500">Name:</span> <span className="font-medium">{order.full_name}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="font-medium">{order.email}</span></div>
                  <div><span className="text-slate-500">Mobile:</span> <span className="font-medium">+91 {order.mobile}</span></div>
                  <div><span className="text-slate-500">Billing State:</span> <span className="font-medium">{form.billing_state || order.billing_state}</span></div>
                </div>

                {/* Amount breakdown */}
                <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal (incl. GST)</span>
                    <span className="font-medium text-[#0A192F]">₹{originalAmount.toLocaleString("en-IN")}</span>
                  </div>
                  {couponApplied && couponDiscount > 0 && (
                    <div className="flex justify-between text-emerald-700" data-testid="apply-discount-line">
                      <span>Promo discount ({couponApplied})</span>
                      <span className="font-medium">- ₹{couponDiscount.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                    <span className="text-sm text-slate-500">Total payable</span>
                    <span className="font-display font-bold text-xl text-[#0A192F]" data-testid="apply-final-amount">₹{finalAmount.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 rounded-md border border-blue-200 bg-blue-50 flex items-start gap-2 text-xs text-slate-700">
                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#1E3A8A]" />
                Secure payment via Cashfree Payments. Your card details never touch our servers — status is verified server-side before your order is marked paid.
              </div>

              {/* Pay Now at top */}
              <Button onClick={pay} disabled={busy} className="mt-4 bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6 w-full sm:w-auto" data-testid="apply-pay-btn">
                <IndianRupee className="w-4 h-4 mr-1" /> {busy ? "Processing…" : `Pay ₹${finalAmount.toLocaleString("en-IN")}`}
              </Button>

              {/* GST / Promo toggle rows */}
              <div className="mt-6 space-y-4">
                {/* Row 1: GST */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm" data-testid="pay-gst-panel">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="shrink-0 w-9 h-9 rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#0A192F] uppercase tracking-wide">Do you have GST Number?</div>
                        <div className="text-xs text-slate-500 mt-0.5">We'll include your GSTIN on the tax invoice.</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-bold uppercase ${hasGst ? "text-[#1E3A8A]" : "text-slate-400"}`}>{hasGst ? "Yes" : "No"}</span>
                      <Switch checked={hasGst} onCheckedChange={(v) => { setHasGst(v); if (!v) setGstin(""); }} data-testid="pay-gst-toggle" />
                    </div>
                  </div>
                  {hasGst && (
                    <div className="mt-4">
                      <Label>GSTIN</Label>
                      <Input
                        value={gstin}
                        onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        placeholder="27ABCDE1234F1Z5"
                        maxLength={15}
                        className="font-mono uppercase h-11"
                        data-testid="pay-gstin-input"
                      />
                      <div className="text-xs text-slate-500 mt-1">15-character GSTIN format (validated by server)</div>
                    </div>
                  )}
                </div>

                {/* Row 2: Promo */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm" data-testid="pay-promo-panel">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Percent className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#0A192F] uppercase tracking-wide">Do you have a Promo Code?</div>
                        <div className="text-xs text-slate-500 mt-0.5">Apply an active coupon to reduce the total.</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-bold uppercase ${hasPromo ? "text-emerald-700" : "text-slate-400"}`}>{hasPromo ? "Yes" : "No"}</span>
                      <Switch checked={hasPromo} onCheckedChange={(v) => { setHasPromo(v); if (!v) removeCoupon(); }} data-testid="pay-promo-toggle" />
                    </div>
                  </div>
                  {hasPromo && (
                    <div className="mt-4">
                      {couponApplied ? (
                        <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="pay-coupon-applied">
                          <div>
                            <span className="inline-block bg-white border border-emerald-300 text-emerald-800 font-mono text-xs px-2 py-1 rounded mr-2">{couponApplied}</span>
                            <span className="text-emerald-800">Applied — you save <b>₹{couponDiscount.toLocaleString("en-IN")}</b></span>
                          </div>
                          <button type="button" className="text-xs text-red-600 hover:underline font-medium" onClick={removeCoupon} data-testid="pay-coupon-remove">Remove</button>
                        </div>
                      ) : (
                        <div>
                          <Label>Enter code</Label>
                          <div className="flex gap-2">
                            <Input
                              value={couponInput}
                              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                              placeholder="e.g. WELCOME10"
                              className="uppercase font-mono h-11"
                              data-testid="pay-coupon-input"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={applyCoupon}
                              disabled={applyingCoupon || !couponInput.trim()}
                              className="h-11 px-5"
                              data-testid="pay-coupon-apply"
                            >
                              {applyingCoupon ? "Applying…" : "Apply"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
